import React, { useState, useEffect, useCallback } from 'react';
import { Trans } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useParams, useNavigate } from 'react-router';
import { camelCase } from 'change-case';
import { unwrapResult } from '@reduxjs/toolkit';

import { useAppDispatch, RootState } from '@/store';
import { useStep, Step } from '@/hooks/useStep';
import {
  SubscriptionRequest,
  translateApiErrors,
} from '@/types/subscriptions/request';
import {
  castToRequest,
  pickServiceGroupId,
} from '@/types/subscriptions/subscription';
import { apiStore } from '@/store/api';
import { errorStore } from '@/store/error';
import { organizationSubscriptionStore } from '@/store/organization/subscription';
import { LoaderWithDimmer } from '@/components/utils/LoaderWithDimmer';
import { CenteredLoader } from '@/components/utils/CenteredLoader';
import { pages } from '@/settings/pages';
import i18n from '@/settings/i18n';
import { useFlashMessenger } from '@/hooks/useFlashMessenger';
import { resetErrorState } from '@/store/error/reducer';

import { Input } from '../Presentation/Input';
import { Confirm } from '../Presentation/Confirm';
import { Done } from '../Presentation/Done';
import { SubscriptionForm } from '../../Container';
import { Preview } from '../../Presentation/Preview';
import { DoneContent } from '../../Presentation/DoneContent';
import { useSubscriptionAccessGuard } from '../../hooks/useSubscriptionAccessGuard';

type SnapShot = {
  contactId?: number;
  billingContactId?: number;
};

const getIsConnecting = apiStore.selectors.createConnectingSelector([
  organizationSubscriptionStore.asyncActions.update.typePrefix,
]);
const getCreateSubscriptionError = errorStore.selectors.createErrorSelector(
  organizationSubscriptionStore.asyncActions.update.typePrefix,
);

const selector = (state: RootState) => {
  const { api, organization } = state;

  const e = getCreateSubscriptionError(state.error);
  let translatedErrors = {};
  if (e && e.data && e.status === 422) {
    const { errors } = e.data;
    translatedErrors = translateApiErrors(errors, organization.service.items);
  }

  const subscription = organization.subscription.item;
  const serviceGroupId = subscription ? pickServiceGroupId(subscription) : null;

  return {
    isConnecting: getIsConnecting(api),
    serviceGroupId,
    subscription,
    warnings: organization.subscription.warnings,
    createSubscriptionError: translatedErrors,
  };
};

const contactIdSelector = (state: RootState) => {
  const items = state.organization.contact.items;
  const item = items[0];
  const id = item !== undefined ? item.id : 0;
  const lastItem = items[items.length - 1];

  return {
    contactId: id,
    defaultContactId: lastItem ? lastItem.id : undefined,
  };
};

const billingContactIdSelector = (state: RootState) => {
  const items = state.organization.billingContact.items;
  const item = items[0];
  const id = item !== undefined ? item.id : 0;
  const lastItem = items[items.length - 1];

  return {
    billingContactId: id,
    defaultBillingContactId: lastItem ? lastItem.id : undefined,
  };
};

export const SubscriptionEditContainer: React.FC = () => {
  const { flashError } = useFlashMessenger();
  const {
    isConnecting,
    serviceGroupId,
    warnings,
    subscription,
    createSubscriptionError,
  } = useSelector(selector);
  const { contactId, defaultContactId } = useSelector(contactIdSelector);

  const { billingContactId, defaultBillingContactId } = useSelector(
    billingContactIdSelector,
  );
  const dispatch = useAppDispatch();

  const [subscriptionRequest, setSubscriptionRequest] =
    useState<SubscriptionRequest>();
  const [snapshots, setSnapShots] = useState<SnapShot>({
    contactId: 0,
    billingContactId: 0,
  });
  const { step, stepTo } = useStep();

  const navigate = useNavigate();
  const { requestCode } = useParams<{ requestCode: string }>();
  const generateStepUrl = useCallback(
    (requestCode?: string) => {
      return `/subscription/${requestCode}/edit/${camelCase(Step[step])}`;
    },
    [step],
  );

  const { shouldRedirect } = useSubscriptionAccessGuard({ serviceGroupId });

  useEffect(() => {
    return () => {
      dispatch(resetErrorState());
    };
  }, [dispatch]);

  useEffect(() => {
    dispatch(
      organizationSubscriptionStore.asyncActions.initializeSubscriptionEdit({
        requestCode,
      }),
    ).then(unwrapResult);

    return () => {
      dispatch(organizationSubscriptionStore.actions.clearItemAndWarnings());
    };
  }, [dispatch, requestCode]);

  useEffect(() => {
    if (!subscriptionRequest && subscription) {
      const values = castToRequest(subscription);
      setSubscriptionRequest(values);
      setSnapShots({
        contactId: values.contactId,
        billingContactId: values.billingContactId,
      });
    }
  }, [subscription, subscriptionRequest]);

  useEffect(() => {
    if (shouldRedirect) return;
    // GTM発火用pushState
    // stepの変更でのみ発火してほしいのでdepsはstepのみ
    window.history.pushState(
      { step: Step[step] },
      document.title,
      generateStepUrl(requestCode),
    );
  }, [generateStepUrl, requestCode, shouldRedirect, step]);

  const onSubmitDryRun = useCallback(
    async (subscriptionRequest: SubscriptionRequest) => {
      setSubscriptionRequest(subscriptionRequest);
      await dispatch(
        organizationSubscriptionStore.asyncActions.update({
          subscriptionRequest,
          snapshots,
          requestCode,
          dryRun: true,
        }),
      ).then(unwrapResult);
      // スクロール位置制御のためハッシュ値をクリア
      navigate(
        {
          pathname: window.location.pathname,
          hash: '',
        },
        { replace: true },
      );
      stepTo.confirm();
    },
    [dispatch, snapshots, requestCode, navigate, stepTo],
  );

  const onSubmit = useCallback(async () => {
    if (!subscriptionRequest) {
      return;
    }
    await dispatch(
      organizationSubscriptionStore.asyncActions.update({
        subscriptionRequest,
        snapshots,
        requestCode,
        dryRun: false,
      }),
    )
      .then(unwrapResult)
      .catch((err) => {
        // FIXME: 申込移管リリース後削除したい
        if (err?.isNotFound) {
          flashError(i18n.t('ui.error.unexpected'));
          navigate(pages.top);
        }
        return;
      });

    stepTo.done();
  }, [
    dispatch,
    flashError,
    navigate,
    requestCode,
    snapshots,
    stepTo,
    subscriptionRequest,
  ]);

  // isFetchingに相当
  if (!subscription || !subscriptionRequest) {
    return <CenteredLoader />;
  }

  return (
    <>
      {isConnecting && (
        <LoaderWithDimmer>
          <Trans i18nKey="ui.subscription.spinner">
            <br />
          </Trans>
        </LoaderWithDimmer>
      )}
      {(() => {
        switch (step) {
          case Step.Input:
            return (
              <Input>
                {serviceGroupId && (
                  <SubscriptionForm
                    serviceGroupId={serviceGroupId}
                    values={subscriptionRequest}
                    extraErrors={createSubscriptionError}
                    billingContactId={billingContactId}
                    contactId={contactId}
                    defaultContactId={defaultContactId}
                    defaultBillingContactId={defaultBillingContactId}
                    onSubmit={onSubmitDryRun}
                  />
                )}
              </Input>
            );
          case Step.Confirm:
            return (
              <Confirm>
                <Preview
                  mode="new"
                  isConnecting={isConnecting}
                  subscription={subscription}
                  onBack={stepTo.input}
                  onSubmit={onSubmit}
                  warnings={warnings}
                />
              </Confirm>
            );
          case Step.Done:
            return (
              <Done>
                <DoneContent subscription={subscription} />
              </Done>
            );
        }
      })()}
    </>
  );
};
